<template>
  <div>
    <!-- Trigger Button -->
    <v-btn 
      :disabled="disabled" 
      color="primary" 
      :loading="loading" 
      @click="fetch" 
      text
      class="font-weight-black text-uppercase tracking-widest rounded-lg px-4 transition-fast-in-fast-out h-44"
    >
      <v-icon left size="16">fas fa-file-export</v-icon> 
      <slot>Exportar</slot>
    </v-btn>

    <!-- Download Dialog -->
    <v-dialog v-model="dataReady" max-width="400" transition="scale-transition">
      <v-card class="modern-card overflow-hidden rounded-xl">
        <!-- Premium Header -->
        <div class="bg-grey-light pa-6 border-bottom">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="40" class="mr-4 shadow-sm border-white-sm">
              <v-icon color="white" small>fas fa-download</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-black primary--text lh-1">Exportar Compras</div>
              <div class="caption grey--text font-weight-bold text-uppercase tracking-wider mt-1">Archivo CSV preparado</div>
            </div>
          </div>
        </div>

        <v-card-text class="text-center pa-8">
          <v-avatar color="primary lighten-5" size="80" class="mb-6">
            <v-icon color="primary" size="40">fas fa-shopping-cart</v-icon>
          </v-avatar>
          <div class="body-1 primary--text font-weight-bold mb-2">¡Todo listo para exportar!</div>
          <div class="caption grey--text px-4 mb-6">El registro de órdenes de compra ha sido procesado exitosamente.</div>

          <vue-json-to-csv
            :json-data="items"
            :labels="labels"
            :csv-title="csvTitle"
            :separator="';'"
            @success="val => handleSuccess(val)"
            @error="val => handleError(val)">
            <v-btn
              color="success"
              block
              large
              elevation="0"
              height="48"
              class="font-weight-black text-none rounded-lg shadow-sm mb-3"
            >
              <v-icon left>fas fa-download</v-icon>
              Descargar Lista
            </v-btn>
          </vue-json-to-csv>

          <vue-json-to-csv
            :json-data="items_detail"
            :labels="labels_detail"
            :csv-title="csvTitleDetail"
            :separator="';'"
            @success="val => handleSuccess(val)"
            @error="val => handleError(val)">
            <v-btn
              color="primary"
              block
              large
              elevation="0"
              height="48"
              class="font-weight-black text-none rounded-lg shadow-sm"
            >
              <v-icon left>fas fa-download</v-icon>
              Descargar Detalle
            </v-btn>
          </vue-json-to-csv>
        </v-card-text>

        <v-divider class="mx-8 opacity-10"></v-divider>
        <div class="pa-4 text-center">
          <v-btn text small color="grey" class="text-none font-weight-bold" @click="dataReady = false">Cerrar</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import {fetchPurchase} from '@/api/stocks'
import VueJsonToCsv from 'vue-json-to-csv'
import moment from 'moment'

const totalOrder = (item) => item.details.reduce((acc, val) => acc + parseFloat(val.qty) * parseFloat(val.price), 0)

export default {
  name: "ExportPurchaseOrders",
  props: ['filter', 'options', 'disabled', 'search', 'estatuses'],
  components: { VueJsonToCsv },
  data() {
    return {
      loading: false,
      items: [],
      items_detail: [],
      dataReady: false,
      labels: {
        id: { title: 'Numero' },
        created_at: { title: 'Creada' },
        created_by_full_name: { title: 'Creada por' },
        provider_name: { title: 'Proveedor' },
        payment: { title: 'Cond. Pago' },
        title: { title: 'Referencia' },
        center_name: { title: 'C Costo' },
        work_order: { title: 'OT' },
        moneda: { title: 'Moneda' },
        subtotal: { title: 'Subtotal' },
        discount: { title: 'Descuento' },
        neto: { title: 'Neto' },
        iva: { title: 'IVA' },
        total_order: { title: 'Total' },
        estatus: { title: 'Estatus' },
        approved_at: { title: 'Aprobada' },
        approved_by_full_name: { title: 'Aprobada por' },
        notes: { title: 'Observaciones' },
      },
      labels_detail: {
        id: { title: 'Numero' },
        created_at: { title: 'Creada' },
        estatus: { title: 'Estatus' },
        approved_at: { title: 'Aprobada' },
        work_order: { title: 'OT' },
        provider_name: { title: 'Proveedor' },
        title: { title: 'Referencia' },
        article_sku: { title: 'Codigo' },
        article_name: { title: 'Articulo' },
        qty: { title: 'Cantidad' },
        received: { title: 'Recibido' },
        article_unit_name: { title: 'Unidades' },
        price: { title: 'Precio' },
        subtotal: { title: 'Subtotal' },
        notes: { title: 'Observaciones' },
      },
      csvTitle: `export_ordenesdecompra_${moment(new Date().toISOString()).format('DD-MM-yyyy_HH:mm')}`,
      csvTitleDetail: `export_ordenesdecompra_detalle_${moment(new Date().toISOString()).format('DD-MM-yyyy_HH:mm')}`,
    }
  },
  methods: {
    async fetch() {
      this.loading = true
      if (!this.filter.created_after) this.filter.created_after = ''
      if (!this.filter.created_before) this.filter.created_before = ''
      const { sortBy, sortDesc } = this.options || {}
      let sort = ''
      if (sortBy && sortBy[0] === 'center_name') sort = 'center__name'
      else if (sortBy && sortBy[0] === 'provider_name') sort = 'provider__name'
      else sort = (sortBy && sortBy[0]) || 'id'
      if (!this.filter.estatus) this.filter.estatus = ''
      if (!this.filter.tipo) this.filter.tipo = ''
      const query = `ordering=${sortDesc && sortDesc[0] ? sort : '-' + sort}&search=${this.search || ''}&estatus=${this.filter.estatus}&stock_service=${this.filter.tipo}&created_after=${this.filter.created_after}&created_before=${this.filter.created_before}`
      try {
        const res = await fetchPurchase(query)
        this.items_detail = []
        this.items = (res.results || []).map(item => {
          item.provider_name = `${item.provider_rut} ${item.provider_name}`
          const round2 = (n) => Math.round(n * 100) / 100
          item.subtotal = round2(totalOrder(item))
          item.discount = round2(item.subtotal * item.discount / 100)
          item.neto = round2(item.subtotal - item.discount)
          item.iva = round2(item.neto * item.iva / 100)
          item.total_order = round2(item.neto + item.iva)
          item.created_at = moment(item.created_at).format('DD-MM-YYYY')
          item.approved_at = item.approved_at ? moment(item.approved_at).format('DD-MM-YYYY') : '-'
          item.work_order = item.work_order ? item.work_order : '-'
          item.estatus = this.estatuses.find(i => item.estatus == i.value).text
          item.details.forEach(detail => {
            this.items_detail.push({
              ...detail,
              id: item.id,
              created_at: item.created_at,
              approved_at: item.approved_at,
              work_order: item.work_order,
              provider_name: item.provider_name,
              title: item.title,
              subtotal: Math.round(detail.price * detail.qty * 100) / 100,
              estatus: item.estatus,
              notes: item.notes ? item.notes : '-',
            })
          })
          return item
        })
        this.dataReady = true
      } catch (error) {
        notifications.showError('Error cargando artículos ' + error)
      } finally {
        this.loading = false
      }
    },
    handleSuccess() {
      notifications.showSuccess('Compras Exportadas')
      this.dataReady = false
    },
    handleError() {
      notifications.showError('Error Exportando Compras')
      this.dataReady = false
    }
  }
}
</script>

<style scoped>
.modern-card { border-radius: 12px !important; font-family: 'Outfit', sans-serif !important; }
.bg-grey-light { background-color: #f8fafc !important; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.06) !important; }
.border-white-sm { border: 2px solid white !important; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.05) !important; }
.h-44 { height: 44px !important; }
.lh-1 { line-height: 1 !important; }
.tracking-widest { letter-spacing: 1.5px; }
.tracking-wider { letter-spacing: 0.8px; }
</style>